import {
  type ChainType,
  chainTypeEntries,
  type WalletGroupedAccounts,
} from '@polaris/core/models'
import { walletFlagsMapAtom } from '@polaris/core/stores'
import { shorten } from '@polaris/core/utils'
import { useAtom, useAtomValue } from 'jotai'
import { ArrowLeft, ArrowRight, X } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import type React from 'react'
import type { ComponentProps } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useMeasure from 'react-use/lib/useMeasure'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/src/components/ui/dialog'
import { NoSearchResults } from '@/src/components/ui/no-search-results'
import { SearchField } from '@/src/components/ui/search-field'
import { useDisclosure } from '@/src/hooks/use-disclosure'
import useWalletConnection from '@/src/hooks/wallet/use-wallet-connection'
import {
  useWalletOptions,
  type WalletOption,
} from '@/src/hooks/wallet/use-wallet-options'
import {
  focusedAccountAtom,
  hasAddedWalletAtom,
  isPrivacyDepositContextAtom,
  newlyAddedWalletNameAtom,
  walletAccountsAtom,
} from '@/src/stores/accounts/wallets'
import { selectWalletsDialogWatchAddressAtom } from '@/src/stores/select-wallets'
import { cn } from '@/src/utils/styles'
import { ChangeAccount } from '../accounts/change-account'
import {
  AddPendingAccount,
  WalletAddedSuccess,
} from '../accounts/detected-pending-account'
import { ManageWallet } from '../accounts/manage-wallet'
import {
  WatchAddressIcon,
  WatchWalletAddressScreen,
} from '../accounts/watch-address'
import { Button } from '../ui/button'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideDescription,
  ListRowSideName,
} from '../ui/list-row'
import { CurrentConnectionWalletState } from './current-connection-wallet-state'
import { NewlyAddedWallet } from './newly-added-wallet'
import { WalletCard } from './wallet-card'
import { WalletListRow } from './wallet-list-row'

const TOP_WALLET_OPTIONS_COUNT = 6

interface ConnectWalletsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  chainType?: ChainType
}

interface ConnectWalletsDialogContentProps extends ConnectWalletsDialogProps {}

interface MoreOptionsCardProps {
  remainingWallets: Array<{
    displayName: string
    logoUrl?: string
  }>
  onClick: () => void
}

function MoreOptionsCard({ remainingWallets, onClick }: MoreOptionsCardProps) {
  return (
    <button
      onClick={onClick}
      className='aspect-[42/23] rounded-2xl border border-border1 p-3 pb-2 flex flex-col gap-2 w-full justify-between text-left transition-colors hover:bg-surface1'
    >
      <div className='flex items-center gap-2'>
        {remainingWallets
          .slice(0, 4)
          .map(
            (wallet) =>
              wallet.logoUrl && (
                <Image
                  key={wallet.displayName}
                  className='rounded-md overflow-hidden'
                  src={wallet.logoUrl}
                  alt={`${wallet.displayName} logo`}
                  width={24}
                  height={24}
                  unoptimized
                />
              ),
          )}
      </div>
      <div className='flex flex-col text-sm tracking-tight text-left'>
        <div className='text-primary font-semibold text-ellipsis overflow-hidden whitespace-nowrap'>
          More options
        </div>
        <div className='text-link h-5'>See all</div>
      </div>
    </button>
  )
}

export function useConnectWalletsDialogContent({
  open,
  chainType,
  onOpenChange,
}: ConnectWalletsDialogContentProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showWatchWalletScreen, setShowWatchWalletScreen] = useState(false)
  const {
    connectingWallet: [connectingWallet, setConnectingWallet],
    changingAccountWallet: [changingAccountWallet, setChangingAccountWallet],
    searchQuery: [searchQuery, setSearchQuery],
    showMoreOptions: [showMoreOptions, setShowMoreOptions],
    selectedPendingWallet: [
      selectedPendingWallet,
      setSelectedPendingWalletName,
    ],
    pendingWalletAddSuccess,
    isAddingPendingWallet,
    addPendingWallet,
    onSelectWallet,
    resetState,
  } = useWalletConnection()

  const [hasAddedWallet, setHasAddedWallet] = useAtom(hasAddedWalletAtom)
  const [newlyAddedWalletName, setNewlyAddedWalletName] = useAtom(
    newlyAddedWalletNameAtom,
  )
  const [focusedAccount, setFocusedAccount] = useAtom(focusedAccountAtom)
  const isPrivacyDepositContext = useAtomValue(isPrivacyDepositContextAtom)

  const {
    open: openWalletExplanationModal,
    close: closeWalletExplanationModal,
    isOpen: isWalletExplanationModalOpen,
  } = useDisclosure()

  const {
    connectedWallets: connectedWalletOptions,
    aggregatedWallets,
    installableWallets,
  } = useWalletOptions({
    searchWalletQuery: showMoreOptions ? searchQuery : undefined,
    reportConnectingWalletInfo: setConnectingWallet,
  })
  const { accountsWithWallets, groupedAccounts } =
    useAtomValue(walletAccountsAtom)

  const walletFlags = useAtomValue(walletFlagsMapAtom)
  const watchAddress = useAtomValue(selectWalletsDialogWatchAddressAtom)

  const foundFocusedAccount = useMemo(() => {
    if (!focusedAccount) return
    return groupedAccounts.find(
      (account) => account.grouping_key === focusedAccount.grouping_key,
    )
  }, [focusedAccount, groupedAccounts])

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [])

  const newlyAddedWalletAccount = useMemo(() => {
    if (!newlyAddedWalletName) return
    return groupedAccounts.find(
      (groupedAccount) =>
        groupedAccount.provider_info.name === newlyAddedWalletName,
    )
  }, [groupedAccounts, newlyAddedWalletName])

  // Sort wallets to prioritize Keplr, MetaMask, and Phantom
  const { initialWalletOptions, allWalletOptions, remainingWalletOptions } =
    useMemo(() => {
      const priorityWallets = ['Keplr', 'MetaMask', 'Phantom', 'Rabby', 'Leap']
      let filteredWallets = [...aggregatedWallets]

      // Filter by chainType if present
      if (chainType !== undefined) {
        filteredWallets = filteredWallets.filter(
          (wallet) => wallet.connectors[chainType] !== undefined,
        )
      }

      const sortedWallets = filteredWallets.sort((a, b) => {
        const aIndex = priorityWallets.indexOf(a.displayName)
        const bIndex = priorityWallets.indexOf(b.displayName)
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1
        return 0
      })
      const connectableOptions = sortedWallets.filter(
        (wallet) => Object.values(wallet.connectors).length > 0,
      )
      const installableOptions =
        chainTypeEntries(installableWallets)?.reduce(
          (acc, [_chainType, wallets]) => {
            wallets.forEach((w) => {
              // Only add if not already in connectableOptions and not already in acc
              const isConnectable = connectableOptions.some(
                (c) => c.displayName === w.displayName,
              )
              const isAlreadyAdded = acc
                ? acc.some((a) => a.displayName === w.displayName)
                : false
              if (!isConnectable && !isAlreadyAdded && acc) {
                acc.push(w)
              }
            })
            return acc
          },
          [] as (typeof installableWallets)[keyof typeof installableWallets],
        ) ?? []

      const filterOptions = (wallets: WalletOption[]) => {
        return wallets.filter(
          (wallet) =>
            walletFlags[wallet.displayName] ||
            polarisAccountsWalletNames.includes(wallet.displayName),
        )
      }
      // If a user had a prior account created before the wallet whitelist was introduced
      // we should still let them select it.
      const polarisAccountsWalletNames = accountsWithWallets
        .map((account) =>
          'wallet' in account ? account.wallet.displayName : undefined,
        )
        .filter(Boolean)

      const mixedWalletOptions = filterOptions([
        ...connectableOptions,
        ...installableOptions,
      ])

      const initialWalletOptions = filterOptions(connectableOptions).slice(
        0,
        TOP_WALLET_OPTIONS_COUNT,
      )

      const remainingWalletOptions = mixedWalletOptions.slice(
        Math.min(TOP_WALLET_OPTIONS_COUNT, initialWalletOptions.length),
      )

      return {
        initialWalletOptions,
        allWalletOptions: mixedWalletOptions,
        remainingWalletOptions,
      }
    }, [
      aggregatedWallets,
      installableWallets,
      chainType,
      walletFlags,
      accountsWithWallets,
    ])

  const hasNoInstalledWallets = aggregatedWallets.length === 0
  const hasAddedFirstWallet =
    allWalletOptions.filter(
      (wallet) => 'disconnect' in wallet && wallet.disconnect !== undefined,
    ).length === 1

  const [connectingWalletRef, connectingWalletMeasurement] =
    useMeasure<HTMLDivElement>()

  const resetStateWithWatch = useCallback(() => {
    resetState()
    setShowWatchWalletScreen(false)
  }, [resetState])

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      resetStateWithWatch()
    }
  }, [open, resetStateWithWatch])

  const isWatchMode = !!watchAddress

  const computedHeaderMiddle = useMemo<React.ReactNode>(() => {
    if (!isWatchMode) return undefined
    return (
      <div className='flex items-center gap-3 mx-auto py-2'>
        <WatchAddressIcon size='xs' />
        <span className='text-primary font-semibold'>
          {shorten(watchAddress!)}
        </span>
      </div>
    )
  }, [isWatchMode, watchAddress])

  const computedTitle = useMemo<React.ReactNode>(() => {
    return isWatchMode ? 'Connect your watched wallet' : 'Connect wallet'
  }, [isWatchMode])

  const computedDescription = useMemo<React.ReactNode>(() => {
    if (hasAddedFirstWallet)
      return 'Keep track of all of your wallets in one simple, unified portfolio.'

    return isWatchMode
      ? 'Trade from your watched wallet by connecting it to your portfolio.'
      : !hasNoInstalledWallets
        ? 'Connect and add wallets to your portfolio.'
        : 'It looks like you don’t have any wallets installed…'
  }, [hasAddedFirstWallet, hasNoInstalledWallets, isWatchMode])

  const installableMarqueeWallets = useMemo(() => {
    const wallets = new Set(
      Object.values(installableWallets)
        .flat()
        .filter((wallet) => wallet !== undefined),
    )

    return Array.from(wallets)
  }, [installableWallets])

  if (showWatchWalletScreen) {
    return {
      dialogContentProps: {
        className: 'sm:max-w-[28rem] min-h-[unset] h-fit pt-0',
        showCloseButton: true,
        showBackButton: true,
        backAction: () => setShowWatchWalletScreen(false),
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <DialogTitle className='hidden' />
          <DialogDescription className='hidden' />
          <WatchWalletAddressScreen onClose={() => onOpenChange(false)} />
        </>
      ),
      isInitialState: false,
      isWatchMode: true,
      initialWalletOptions,
    }
  }

  if (foundFocusedAccount) {
    return {
      dialogContentProps: {
        className:
          'sm:max-w-[28rem] w-full max-h-[80vh] h-[unset] flex flex-col',
        showCloseButton: true,
        closeAction: () => {
          onOpenChange(false)
          setHasAddedWallet(false)
          setNewlyAddedWalletName(null)
          setFocusedAccount(null)
          resetState()
        },
        middle: (
          <DialogTitle className='text-xl font-semibold'>
            Manage wallet
          </DialogTitle>
        ),
        backAction: () => {
          setFocusedAccount(null)
        },
        showBackButton: true,
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <DialogDescription className='hidden' />
          <div className='w-full p-8 pt-0'>
            <ManageWallet
              wallet={foundFocusedAccount}
              onBack={() => setFocusedAccount(null)}
              classNames={{
                buttonsContainer: 'pb-0',
              }}
            />
          </div>
        </>
      ),
      isInitialState: false,
      initialWalletOptions,
    }
  }

  // Show "Ready to trade" modal (skip in privacy deposit flow)
  if (hasAddedWallet && !isPrivacyDepositContext) {
    return {
      dialogContentProps: {
        className:
          'sm:max-w-[28rem] w-full max-h-[80vh] h-[unset] flex flex-col',
        showCloseButton: true,
        closeAction: () => {
          onOpenChange(false)
          setHasAddedWallet(false)
          setNewlyAddedWalletName(null)
          resetState()
        },
      } satisfies ComponentProps<typeof DialogContent>,
      content: <NewlyAddedWallet onOpenChange={onOpenChange} />,
      isInitialState: false,
      initialWalletOptions,
    }
  }

  if (
    selectedPendingWallet ||
    pendingWalletAddSuccess ||
    newlyAddedWalletAccount
  ) {
    return {
      dialogContentProps: {
        className: cn('sm:max-w-[28rem] min-h-[unset] h-fit pt-0', {
          'bg-green-800': pendingWalletAddSuccess || newlyAddedWalletAccount,
        }),
        showBackButton: !!selectedPendingWallet,
        showCloseButton: true,
        backAction: () => {
          setSelectedPendingWalletName(null)
          setChangingAccountWallet(undefined)
          setNewlyAddedWalletName(null)
        },
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <DialogTitle className='hidden' />
          <DialogDescription className='hidden' />
          <div className='text-center h-fit px-8'>
            {pendingWalletAddSuccess || newlyAddedWalletAccount ? (
              <WalletAddedSuccess
                successAccount={
                  pendingWalletAddSuccess ||
                  (newlyAddedWalletAccount as WalletGroupedAccounts)
                }
                onClose={() => {
                  setSelectedPendingWalletName(null)
                  setChangingAccountWallet(undefined)
                  // In privacy flow: close dialog immediately (skip "Ready to trade")
                  if (isPrivacyDepositContext) {
                    onOpenChange(false)
                    setHasAddedWallet(false)
                    setNewlyAddedWalletName(null)
                    resetState()
                  } else {
                    setHasAddedWallet(true)
                    resetState()
                  }
                }}
              />
            ) : selectedPendingWallet ? (
              <AddPendingAccount
                account={selectedPendingWallet}
                isAddingAccount={isAddingPendingWallet}
                onAdd={addPendingWallet}
                onCancel={() => {
                  setSelectedPendingWalletName(null)
                  setChangingAccountWallet(undefined)
                }}
              />
            ) : null}
          </div>
        </>
      ),
      isInitialState: false,
      initialWalletOptions,
    }
  }

  if (connectingWallet) {
    const aggregatedWallet = aggregatedWallets.find(
      (wallet) => wallet.displayName === connectingWallet.name,
    )

    if (!aggregatedWallet) {
      resetState()
      return null
    }

    const totalEcosystemCount = Object.keys(aggregatedWallet.connectors).length
    const connectedEcosystemCount =
      Object.keys(aggregatedWallet.disconnectors).length + 1

    return {
      dialogContentProps: {
        className: 'sm:max-w-[28rem] min-h-[unset] pt-0',
        showCloseButton: true,
        showBackButton: true,
        backAction: () => resetState(),
        style: {
          height: connectingWalletMeasurement.height + 48 * 2,
        },
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <DialogTitle className='hidden' />
          <DialogDescription className='hidden' />
          <div ref={connectingWalletRef} className='px-8'>
            <CurrentConnectionWalletState
              className='max-w-md mx-auto p-0'
              titleClassName='text-4xl font-serif font-extralight'
              errorType={connectingWallet.error?.type}
              onRetry={async () => onSelectWallet(aggregatedWallet)}
              status={connectingWallet.status}
              totalEcosystemCount={totalEcosystemCount}
              connectedEcosystemCount={connectedEcosystemCount}
              walletIcon={connectingWallet.icon}
              walletName={connectingWallet.name}
              statusCopy={{
                connecting: `Confirm the connection to Polaris in your ${connectingWallet.name} browser extension.`,
              }}
            />
          </div>
        </>
      ),
      isInitialState: false,
      initialWalletOptions,
    }
  }

  if (changingAccountWallet) {
    const wallet = Object.values(connectedWalletOptions).find(
      (wallet) => wallet.displayName === changingAccountWallet.displayName,
    )

    if (!wallet) {
      resetState()
      return null
    }

    let accountName = wallet.accountName
    if (!accountName) {
      const account = accountsWithWallets.find(
        (account) =>
          'wallet' in account &&
          account.wallet.displayName === wallet?.displayName,
      )
      accountName = account?.name ?? wallet?.displayName
    }

    return {
      dialogContentProps: {
        className: 'sm:max-w-[28rem] min-h-[unset] pt-0 h-fit',
        showCloseButton: true,
        showBackButton: true,
        backAction: () => resetState(),
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <DialogTitle className='hidden' />
          <DialogDescription className='hidden' />
          <div className='flex items-center justify-center h-full px-8'>
            <ChangeAccount
              className='max-w-md mx-auto'
              leftWalletName={wallet.displayName}
              leftWalletIcon={wallet.logoUrl}
              leftAccountName={accountName || wallet.displayName}
              rightWalletName={wallet.displayName}
              rightWalletIcon={wallet.logoUrl}
              rightAccountName={wallet.displayName}
              connectButtonOnClick={() => resetState()}
            />
          </div>
        </>
      ),
      isInitialState: false,
      initialWalletOptions,
    }
  }

  if (isWalletExplanationModalOpen) {
    return {
      dialogContentProps: {
        className: 'sm:max-w-[28rem] w-full h-[unset]',
        hideHeader: true,
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className='flex flex-col'
        >
          <div className='flex w-full items-center justify-between absolute z-10 top-0 h-20 px-8'>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              onClick={closeWalletExplanationModal}
              className='size-8'
            >
              <ArrowLeft size={24} className='text-[#0A0F0D] min-w-6' />
            </Button>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              onClick={closeWalletExplanationModal}
              className='size-8'
            >
              <X size={24} className='text-[#0A0F0D] min-w-6' />
            </Button>
          </div>
          <div className='h-[224px] bg-orange-200 w-full relative overflow-hidden'>
            <Image
              src={'https://s3.polaris.app/artworks/wallets-on-log.png'}
              alt=''
              width={400}
              height={230}
              className='absolute top-5 left-5'
              unoptimized
            />
          </div>
          <div className='flex flex-col items-start p-8 pb-32 gap-8 max-h-[430px] overflow-y-auto no-scrollbar'>
            <div className='flex flex-col items-start gap-4'>
              <DialogTitle className='font-serif font-extralight'>
                Choosing a crypto wallet
              </DialogTitle>
              <span>
                A crypto wallet is like a digital keychain that lets you store,
                send, and receive crypto tokens. But instead of holding tokens
                directly, it holds the keys to access them on a network (also
                referred to as a <span className='italic'>chain</span>). <br />
                <br />
                Depending on your needs and priorities, there’s a few things to
                consider when choosing a wallet:
              </span>
            </div>
            <div className='flex flex-col gap-2'>
              <h6 className='text-xl font-semibold'>
                Which tokens are you holding?
              </h6>
              <span>
                There are a few different types of networks in crypto — like
                different countries with their own rules and languages. So,
                wallets are often designed to work with specific networks or
                ecosystems, but increasingly more wallets are becoming
                cross-compatible. <br />
                <br />
                Pick a wallet that supports your tokens and the networks they
                are on.
              </span>
            </div>
            <div className='flex flex-col gap-2'>
              <h6 className='text-xl font-semibold'>
                Self-custody vs custodial?
              </h6>
              <span>
                When you buy crypto on an exchange such as Coinbase, Binance,
                etc. you don’t have custody of your tokens — similar to how a
                bank works. This means you’re trusting a third-party with your
                funds, which presents various risks. <br />
                <br /> Most wallet users prefer to choose wallets that offer
                self-custody, which means you hold the keys to your crypto. Just
                make sure to safely backup your wallet!
              </span>
            </div>
            <div className='flex flex-col gap-2'>
              <h6 className='text-xl font-semibold'>Ease of use</h6>
              <span>
                Some wallets have more functionality than others, which can be
                overwhelming and even unnecessary for beginners, but invaluable
                to more advanced users. Choose a wallet that suits your needs by
                trying out as many as you wish.
              </span>
            </div>
            <div className='relative flex items-center rounded-2xl bg-surface1 w-full min-h-[132px]'>
              <div className='flex flex-col justify-between p-5 h-full gap-8'>
                <span className='text-3xl font-extralight font-serif'>
                  Ready to get a wallet?
                </span>
                <Button
                  type='button'
                  onClick={() => {
                    closeWalletExplanationModal()
                    setShowMoreOptions(true)
                  }}
                >
                  <span>Browse wallets</span>
                  <ArrowRight size={24} className='min-w-6' />
                </Button>
              </div>
              <Image
                src='https://s3.polaris.app/illustrations/pouch-01.png'
                alt=''
                width={96}
                height={96}
              />
            </div>
          </div>
          <div className='flex absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-foreground via-foreground via-80% to-transparent p-8 pt-4 z-10'>
            <Button
              type='button'
              variant='primary'
              className='w-full'
              onClick={closeWalletExplanationModal}
            >
              Done
            </Button>
          </div>
        </motion.div>
      ),
      isInitialState: false,
      initialWalletOptions,
    }
  }

  // Grid view for initial wallet selection
  if (!showMoreOptions) {
    return {
      dialogContentProps: {
        className:
          'sm:max-w-[28rem] w-full h-[unset] flex flex-col space-y-3 overflow-hidden',
        showCloseButton: true,
        middle: isWatchMode ? computedHeaderMiddle : undefined,
      } satisfies ComponentProps<typeof DialogContent>,
      content: (
        <>
          <div className='space-y-3'>
            <DialogTitle className='font-serif font-extralight'>
              {isWatchMode ? (
                computedTitle
              ) : !hasNoInstalledWallets && !hasAddedFirstWallet ? (
                <>
                  Start your trading <span className='italic'>adventure</span>
                </>
              ) : (
                `Add ${hasAddedFirstWallet ? 'more' : 'your'} wallets`
              )}
            </DialogTitle>
            <DialogDescription className='pb-8 text-base'>
              {computedDescription}
            </DialogDescription>
          </div>

          {!hasNoInstalledWallets ? (
            <div className='flex flex-col h-full px-8 overflow-y-auto no-scrollbar pb-32 rounded-b-[32px] max-h-[548px]'>
              <div className='grid grid-cols-2 gap-2'>
                {initialWalletOptions.map((wallet) => {
                  const aggregatedWallet = aggregatedWallets.find(
                    (w) => w.displayName === wallet.displayName,
                  )
                  const isConnectable = 'connectors' in wallet
                  const accounts = groupedAccounts.filter(
                    (group) => group.provider_info.name === wallet.displayName,
                  )
                  return (
                    <WalletCard
                      key={wallet.id}
                      wallet={wallet}
                      isConnectable={isConnectable}
                      isConnected={Boolean(accounts.length)}
                      onConnect={() => {
                        if (!aggregatedWallet) return

                        onSelectWallet(aggregatedWallet)
                      }}
                    />
                  )
                })}
                {(remainingWalletOptions.length > 0 || !chainType) && (
                  <MoreOptionsCard
                    remainingWallets={
                      // Don't show watch wallet if a specific ecosystem is specified
                      chainType
                        ? remainingWalletOptions
                        : [
                            {
                              displayName: 'Watch wallet',
                              logoUrl:
                                'https://s3.polaris.app/illustrations/binoculars.png',
                            },
                          ].concat(remainingWalletOptions)
                    }
                    onClick={() => setShowMoreOptions(true)}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className='px-8 flex flex-col pb-8'>
              <div className='select-none relative overflow-hidden flex w-full h-20 mb-8 before:content-[""] before:absolute before:w-12 before:bg-gradient-to-r before:from-foreground before:to-transparent before:-left-1 before:h-20 before:z-10 after:absolute after:w-12 after:bg-gradient-to-l after:from-foreground after:to-transparent after:-right-0.5 after:h-20'>
                <div
                  className='absolute flex items-center left-0 h-20 gap-3 animate-marquee'
                  style={{
                    // @ts-expect-error
                    '--marquee-elements': installableMarqueeWallets.length,
                    '--marquee-elements-m1':
                      installableMarqueeWallets.length - 1,
                    '--marquee-elements-gap': '12px',
                    '--marquee-element-size': '32px',
                  }}
                >
                  {installableMarqueeWallets
                    .concat(installableMarqueeWallets)
                    .map(({ displayName, id, logoUrl, website }, i) => {
                      const Comp = website ? Link : 'div'
                      return (
                        <Comp
                          href={website ?? '#'}
                          key={`${id}-${i}`}
                          target='_blank'
                          className='size-8 rounded-2xl bg-green-200 relative'
                        >
                          <Image
                            alt={displayName}
                            src={logoUrl}
                            fill
                            draggable={false}
                          />
                        </Comp>
                      )
                    })}
                </div>
              </div>
              <div className='flex flex-col gap-2 w-full'>
                <Button
                  type='button'
                  variant='primary'
                  className='w-full font-semibold'
                  onClick={() => setShowMoreOptions(true)}
                >
                  Browse wallets
                </Button>
                <Button
                  type='button'
                  variant='secondary'
                  className='w-full font-semibold'
                  onClick={openWalletExplanationModal}
                >
                  Learn about wallets
                </Button>
                <Button
                  type='button'
                  className='w-full font-semibold mt-2'
                  size='lg'
                  onClick={() => {
                    onOpenChange(false)
                  }}
                >
                  Skip for now
                </Button>
              </div>
            </div>
          )}
        </>
      ),
      isInitialState: true,
      initialWalletOptions,
    }
  }

  // List view with search for all wallets
  return {
    dialogContentProps: {
      className: 'sm:max-w-[28rem] w-full max-h-[80vh] h-[unset] flex flex-col',
      showCloseButton: true,
      showBackButton: true,
      backAction: () => resetState(),
      middle: (
        <SearchField
          dimension='medium'
          className='w-full px-4'
          inputClassName='w-full placeholder:font-normal'
          inputProps={{
            placeholder: 'Search wallets',
            value: searchQuery,
            onChange: (e) => setSearchQuery(e.target.value),
            autoFocus: true,
          }}
          resetState={() => setSearchQuery('')}
        />
      ),
    } satisfies ComponentProps<typeof DialogContent>,
    content: (
      <>
        <DialogTitle className='hidden' />
        <DialogDescription className='hidden' />

        <div
          ref={scrollContainerRef}
          className='flex flex-col h-full px-8 overflow-y-auto no-scrollbar pb-8 -mx-4 rounded-b-[32px]'
        >
          <ListRowButton
            size='lg'
            onClick={() => setShowWatchWalletScreen(true)}
          >
            <div className='flex items-center gap-4'>
              <Image
                src='https://s3.polaris.app/illustrations/binoculars.png'
                alt='Watch wallet'
                width={48}
                height={48}
              />
              <ListRowSide>
                <ListRowSideName>Watch a wallet</ListRowSideName>
                <ListRowSideDescription>
                  Add a view-only wallet
                </ListRowSideDescription>
              </ListRowSide>
            </div>
            <ListRowSide showIndicator />
          </ListRowButton>
          {allWalletOptions.length > 0 ? (
            allWalletOptions?.map((wallet) => (
              <WalletListRow
                key={wallet.id}
                wallet={wallet}
                onSelectWallet={(wallet) => onSelectWallet(wallet)}
              />
            ))
          ) : (
            <NoSearchResults failedQuery='' />
          )}
        </div>
      </>
    ),
    isInitialState: false,
    initialWalletOptions,
  }
}

export function ConnectWalletsDialog({
  open,
  onOpenChange,
  chainType,
}: ConnectWalletsDialogProps) {
  const result = useConnectWalletsDialogContent({
    open,
    onOpenChange,
    chainType,
  })

  if (!result) {
    return null
  }

  const { dialogContentProps, content } = result

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent {...dialogContentProps}>{content}</DialogContent>
    </Dialog>
  )
}
